import { ApiBaseService, ApiV2Service } from './api.base.service';
import store from '@/store/store';
import { SearchResponse } from './task.service';
import {CommercialAccount, Organisation, SuccessResponse, User} from '../types';
import Transformation from '../classes/transformation';

export type SearchUsersResponse = { users: User[] } & SearchResponse;
export type DaizyToken = string;

export class UserService {
  private api: ApiBaseService;

  constructor() {
    this.api = ApiV2Service;
  }

  public async ping() {
    return this.api.get('user/ping');
  }

  public async getOrganisationUsers(): Promise<SearchUsersResponse> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.api.get(`organisation/${organisationId}/users`);
  }

  public async getUsers(
    take: number = 0, skip: number = 0, sort: string = '',
    desc: boolean = false, filters: Record<string, Record<string, boolean>> = {},
    searchNames: string | null = null,
    projectId: number | null,
  ): Promise<SearchUsersResponse> {
    const organisation = store.getters.activeOrganisation[0].id;

    let filtersString = '';

    const filterQuery = Transformation.filterMapToQueryString(filters);

    if (filterQuery) {
      filtersString += `&${filterQuery}`;
    }

    if (projectId) {
      filtersString += `&projectId=${projectId}`;
    }
    if (searchNames) {
      filtersString += `&search=${searchNames}`;
    }
    return this.api.get(
      `organisation/${organisation}/users?` +
      `take=${take}&skip=${skip}&sort=${sort}&desc=${desc}&includeColumnFilters=true${filtersString}`);
  }

  public async getUsersForOrganisation(includeApiUsers: boolean = false): Promise<SearchUsersResponse> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.get( `organisation/${organisation}/users?includeApiUsers=${includeApiUsers}`);
  }

  public async getUser(id: number): Promise<SearchUsersResponse> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.get(`organisation/${organisation}/users?id=${id}`);
  }

  public async createUser(payload: any): Promise<User> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/user`, payload);
  }

  public async updateUser(id: number, payload: any): Promise<User> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.put(`organisation/${organisation}/user/${id}`, payload);
  }

  public async removeUser(id: number): Promise<SuccessResponse> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.delete(`organisation/${organisation}/user/${id}`);
  }

  public async createApiUser(name: string, roles: number[]): Promise<User> {
    const organisation = store.getters.activeOrganisation[0].id;
    const body = {
      name,
      roles,
    };
    return this.api.post(`organisation/${organisation}/apiUser`, body);
  }

  public async impersonate(userEmail: string): Promise<DaizyToken> {
    return this.api.post(`user/impersonate?userEmail=${userEmail}`, {});
  }

  public async resendPassword(userId: number): Promise<SuccessResponse> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/user/${userId}/resend`, {});
  }

  public async getAccounts(): Promise<CommercialAccount[]> {
    return this.api.get('user/accounts');
  }

  public async getOrganisationsForUserPermissions(permission: string, fields?: string[]): Promise<Organisation[]> {
    const queryParams = new URLSearchParams();
    let queryString = '';

    if (fields && fields.length) {
      queryParams.append('fields', fields.join(','));
    }

    queryString = `?permission=${permission}${queryParams.has('fields') ? '&' + queryParams.toString() : ''}`;

    return this.api.get(`user/organisationForPermissions${queryString}`);
  }
}

export default new UserService();
